"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useActingTenant } from "@/context/ActingTenantContext";
import { isSuperAdmin } from "@/lib/auth";
import { api } from "@/lib/api";
import { useSession } from "next-auth/react";
import { CUESHUB_BRAND } from "@/lib/cueshub-brand";
import type { TenantBranding } from "@/types/organization";

interface Branding {
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  logoUrl: string | null;
  logoCacheKey: number;
}

interface BrandingContextValue extends Branding {
  logoDisplayUrl: string | null;
  setLogoUrl: (url: string | null) => void;
  refreshBranding: () => Promise<void>;
}

const defaultBranding: Branding = {
  primaryColor: CUESHUB_BRAND.primary,
  secondaryColor: CUESHUB_BRAND.secondary,
  fontFamily: "Outfit",
  logoUrl: null,
  logoCacheKey: 0,
};

const BrandingContext = createContext<BrandingContextValue>({
  ...defaultBranding,
  logoDisplayUrl: null,
  setLogoUrl: () => {},
  refreshBranding: async () => {},
});

function brandingFromApi(data: TenantBranding): Omit<Branding, "logoCacheKey"> {
  return {
    primaryColor: CUESHUB_BRAND.primary,
    secondaryColor: CUESHUB_BRAND.secondary,
    fontFamily: data.font_family,
    logoUrl: data.logo_url,
  };
}

function withLogoCacheBust(url: string, cacheKey: number): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${cacheKey}`;
}

/** Avoid hammering /tenants/branding when session metadata churns. */
const BRANDING_MIN_FETCH_MS = 60_000;

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const { actingTenant } = useActingTenant();
  const [branding, setBranding] = useState<Branding>(defaultBranding);
  const fetchGeneration = useRef(0);
  const lastFetch = useRef<{ scopeKey: string; at: number } | null>(null);

  const brandingScopeKey = useMemo(() => {
    if (status !== "authenticated") return null;
    const isSuperAdminUser = isSuperAdmin(session?.user?.roles);
    if (isSuperAdminUser) {
      return actingTenant?.id ?? null;
    }
    const tenantId = session?.user?.tenantId;
    return tenantId || null;
  }, [status, actingTenant?.id, session?.user?.roles, session?.user?.tenantId]);

  const refreshBranding = useCallback(async () => {
    if (!brandingScopeKey) return;

    const now = Date.now();
    if (
      lastFetch.current?.scopeKey === brandingScopeKey &&
      now - lastFetch.current.at < BRANDING_MIN_FETCH_MS
    ) {
      return;
    }

    const generation = ++fetchGeneration.current;
    try {
      const data = await api.get<TenantBranding>("/api/v1/tenants/branding");
      if (generation !== fetchGeneration.current) return;

      lastFetch.current = { scopeKey: brandingScopeKey, at: Date.now() };
      setBranding((prev) => {
        const next = brandingFromApi(data);
        const urlChanged = next.logoUrl !== prev.logoUrl;
        return {
          ...next,
          logoCacheKey: next.logoUrl
            ? urlChanged
              ? prev.logoCacheKey + 1
              : prev.logoCacheKey
            : 0,
        };
      });
    } catch {
      /* keep default branding */
    }
  }, [brandingScopeKey]);

  useEffect(() => {
    if (!brandingScopeKey) {
      setBranding(defaultBranding);
      lastFetch.current = null;
      return;
    }
    void refreshBranding();
  }, [brandingScopeKey, refreshBranding]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--color-primary", branding.primaryColor);
    root.style.setProperty("--color-secondary", branding.secondaryColor);
    root.style.setProperty("--font-primary", branding.fontFamily);
  }, [branding.primaryColor, branding.secondaryColor, branding.fontFamily]);

  const setLogoUrl = useCallback((logoUrl: string | null) => {
    lastFetch.current = null;
    setBranding((prev) => ({
      ...prev,
      logoUrl,
      logoCacheKey: logoUrl ? prev.logoCacheKey + 1 : 0,
    }));
  }, []);

  const logoDisplayUrl = branding.logoUrl
    ? withLogoCacheBust(branding.logoUrl, branding.logoCacheKey)
    : null;

  return (
    <BrandingContext.Provider
      value={{
        ...branding,
        logoDisplayUrl,
        setLogoUrl,
        refreshBranding,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
